import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { CITIES } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, MapPin, Clock, Navigation, BedDouble, CalendarDays } from 'lucide-react';
import { ShareButtons } from '../components/ShareButtons';
import { EventWeather } from '../components/EventWeather';
import { AddToCalendar } from '../components/AddToCalendar';

const MONTHS = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Juni', 'Juli', 'August', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
const DAYS = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];

const fmtDate = (d) => { const [y, m, day] = d.split('-'); return `${day}.${m}.${y}.`; };
const pad = (n) => String(n).padStart(2, '0');

export default function Kalendar() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [current, setCurrent] = useState(() => { const d = new Date(2025, 7, 1); return d; });
  const [city, setCity] = useState('all');
  const [stayFrom, setStayFrom] = useState('');
  const [stayTo, setStayTo] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [e, o] = await Promise.all([
          api.get('/events'),
          api.get('/offers', { params: { typeId: 't3' } })
        ]);
        setEvents(e.data);
        setOffers(o.data);
      } catch (err) {
        console.error('Greška pri učitavanju:', err);
      }
    };
    load();
  }, []);

  const year = current.getFullYear();
  const month = current.getMonth();

  const filteredEvents = useMemo(() => events.filter((e) => {
    if (city !== 'all' && e.city !== city) return false;
    if (stayFrom && stayTo) {
      const evEnd = e.endDate || e.date;
      if (e.date > stayTo || evEnd < stayFrom) return false;
    }
    return true;
  }), [events, city, stayFrom, stayTo]);

  const monthKey = `${year}-${pad(month + 1)}`;
  const eventsInMonth = filteredEvents.filter((e) => e.date.startsWith(monthKey) || (e.endDate || '').startsWith(monthKey));

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // ponedjeljak = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsOnDay = (d) => {
    const key = `${year}-${pad(month + 1)}-${pad(d)}`;
    return filteredEvents.filter((e) => e.date <= key && (e.endDate || e.date) >= key);
  };

  const listToShow = (stayFrom && stayTo) ? filteredEvents : eventsInMonth;
  const sortedList = [...listToShow].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="bg-[#f8f9fd] min-h-screen pb-16">
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-[#0b1a3d] mb-2" data-testid="kalendar-title">{t('calendar')}</h1>
          <p className="text-slate-500 max-w-2xl">
            Interaktivni kalendar događaja Srednjobosanskog kantona. Unesite termin boravka i pronađite događaje i slobodan smještaj.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
        {/* KALENDAR */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="calendar-grid">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0b1a3d]">{MONTHS[month]} {year}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => setCurrent(new Date(year, month - 1, 1))} data-testid="cal-prev"
                  className="border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white h-9 w-9 rounded-md transition-colors duration-200">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setCurrent(new Date(year, month + 1, 1))} data-testid="cal-next"
                  className="border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white h-9 w-9 rounded-md transition-colors duration-200">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 uppercase py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((d, i) => {
                if (d === null) return <div key={`x${i}`} />;
                const evs = eventsOnDay(d);
                return (
                  <div key={d} data-testid={`cal-day-${d}`}
                    className={`min-h-[72px] rounded-lg border p-1.5 text-sm ${evs.length ? 'border-[#001752]/30 bg-[#f0f3fb]' : 'border-slate-100'}`}>
                    <span className={`text-xs font-semibold ${evs.length ? 'text-[#001752]' : 'text-slate-400'}`}>{d}</span>
                    <div className="space-y-1 mt-1">
                      {evs.slice(0, 2).map((e) => (
                        <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`}
                          className="block w-full text-left text-[10px] leading-tight bg-[#001752] text-white rounded px-1.5 py-1 truncate hover:bg-[#0a2a7a] transition-colors duration-200">
                          {e.title}
                        </button>
                      ))}
                      {evs.length > 2 && <span className="text-[10px] text-slate-500">+{evs.length - 2}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* LISTA DOGAĐAJA */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-[#0b1a3d] mb-4">
              {stayFrom && stayTo ? `${t('events')}: ${fmtDate(stayFrom)} – ${fmtDate(stayTo)}` : `${t('events')} – ${MONTHS[month]} ${year}`}
            </h3>
            {sortedList.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-500" data-testid="events-empty">{t('noResults')}</div>
            ) : (
              <div className="space-y-3">
                {sortedList.map((e) => (
                  <button key={e.id} onClick={() => setSelected(e)} data-testid={`event-list-item-${e.id}`}
                    className="w-full bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 text-left hover:shadow-lg hover:shadow-slate-200/60 transition-shadow duration-300">
                    <div className="bg-[#e8ecf7] rounded-lg px-3 py-2 text-center shrink-0 w-16">
                      <p className="text-xl font-bold text-[#001752] leading-none">{e.date.split('-')[2]}</p>
                      <p className="text-[10px] uppercase font-semibold text-slate-500 mt-1">{MONTHS[parseInt(e.date.split('-')[1], 10) - 1].slice(0, 3)}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#0b1a3d] truncate">{e.title}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                        <MapPin className="h-3.5 w-3.5 shrink-0" /> {e.location}, {e.city}
                        <Clock className="h-3.5 w-3.5 shrink-0 ml-2" /> {e.time}h
                      </p>
                    </div>
                    <Badge className="bg-[#e8ecf7] text-[#001752] hover:bg-[#e8ecf7] shrink-0 hidden sm:inline-flex">{e.category}</Badge>
                    <span className="shrink-0 hidden md:inline-flex"><EventWeather lat={e.lat} lng={e.lng} date={e.date} /></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR - TERMIN BORAVKA */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="stay-period-box">
            <h3 className="font-bold text-[#0b1a3d] mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {t('stayPeriod')}</h3>
            <p className="text-xs text-slate-500 mb-4">Unesite termin boravka za pregled događaja i smještajnih kapaciteta u tom periodu.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('from')}</label>
                <Input type="date" value={stayFrom} onChange={(e) => setStayFrom(e.target.value)} className="border-slate-200 mt-1" data-testid="stay-from" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('to')}</label>
                <Input type="date" value={stayTo} onChange={(e) => setStayTo(e.target.value)} className="border-slate-200 mt-1" data-testid="stay-to" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('cities')}</label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="border-slate-200 mt-1" data-testid="event-city-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allCities')}</SelectItem>
                    {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(stayFrom || stayTo || city !== 'all') && (
                <Button variant="outline" onClick={() => { setStayFrom(''); setStayTo(''); setCity('all'); }} data-testid="clear-stay-filter"
                  className="w-full border-slate-200 text-slate-600 hover:text-[#001752] rounded-md">
                  {t('clearFilter')}
                </Button>
              )}
            </div>
          </div>

          {stayFrom && stayTo && (
            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="accommodation-box">
              <h3 className="font-bold text-[#0b1a3d] mb-4 flex items-center gap-2">
                <BedDouble className="h-4 w-4" /> {t('accommodationAvailable')}
              </h3>
              <div className="space-y-3">
                {offers.filter((o) => city === 'all' || o.city === city).map((o) => (
                  <Link key={o.id} to={`/ponuda/${o.id}`} data-testid={`accommodation-${o.id}`}
                    className="flex gap-3 items-center group">
                    <img src={o.image} alt={o.title} className="h-14 w-20 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0b1a3d] truncate group-hover:text-[#001752]">{o.title}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.city}</p>
                      <p className="text-[11px] text-emerald-600 font-semibold">Slobodni kapaciteti • online rezervacija</p>
                    </div>
                  </Link>
                ))}
                {offers.filter((o) => city === 'all' || o.city === city).length === 0 && (
                  <p className="text-sm text-slate-500">{t('noResults')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DETALJI DOGAĐAJA */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="event-dialog">
          {selected && (
            <>
              <img src={selected.image} alt={selected.title} className="w-full h-52 object-cover" />
              <div className="p-6">
                <DialogHeader>
                  <Badge className="bg-[#e8ecf7] text-[#001752] hover:bg-[#e8ecf7] w-fit mb-2">{selected.category}</Badge>
                  <DialogTitle className="text-[#0b1a3d] text-xl">{selected.title}</DialogTitle>
                  <DialogDescription className="text-slate-500">
                    {fmtDate(selected.date)}{selected.endDate && selected.endDate !== selected.date ? ` – ${fmtDate(selected.endDate)}` : ''} • {selected.time}h
                  </DialogDescription>
                </DialogHeader>
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{selected.description}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-4">
                  <MapPin className="h-4 w-4 text-[#001752]" /> {selected.location}, {selected.city}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('weather')}:</span>
                  <EventWeather lat={selected.lat} lng={selected.lng} date={selected.date} showLabel />
                </div>
                <div className="flex gap-2 mt-5">
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`} target="_blank" rel="noreferrer" className="flex-1" data-testid="event-gps-btn">
                    <Button className="w-full bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
                      <Navigation className="h-4 w-4 mr-2" /> {t('gpsNavigate')}
                    </Button>
                  </a>
                  <Link to="/mapa" className="flex-1">
                    <Button variant="outline" className="w-full border-[#001752] text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200">
                      {t('viewMap')}
                    </Button>
                  </Link>
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <AddToCalendar event={selected} />
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <ShareButtons url={`${window.location.origin}/kalendar`} text={`${selected.title} – ${fmtDate(selected.date)}, ${selected.location}, ${selected.city}`} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
